import { PureCloudService } from "../../../services/purecloud.service";

import { IQueueStrategy } from "../../../../core-ui/models/strategies";
import { IQueue } from "../../../../core-ui";

export class GenesysCloudQueueStrategy implements IQueueStrategy {
  // memoise the promise, not the result: the share-link sms and email
  // components both ask for queues in the same tick and would otherwise each
  // trigger the full (paginated, twice over) fetch.
  private queuesPromise: Promise<IQueue[]>;

  constructor(private pureCloudService: PureCloudService) {}

  async getAllQueues(): Promise<IQueue[]> {
    return this.pureCloudService.getQueues();
  }

  getMyQueues(): Promise<IQueue[]> {
    if (!this.queuesPromise) {
      this.queuesPromise = this.loadMyQueues();
    }
    return this.queuesPromise;
  }

  /**
   * Warm up the cache in the background, so that the queues are already there
   * by the time the agent opens the share menu. Safe to call repeatedly.
   */
  prefetch(): void {
    this.getMyQueues().catch(() => {
      /* handled in loadMyQueues */
    });
  }

  async getEmailQueues() {
    const queues = await this.getMyQueues();
    return queues.filter((q) => "outboundEmailAddress" in q);
  }

  async getSMSQueues() {
    const queues = await this.getMyQueues();
    return queues.filter((q) => "outboundMessagingAddresses" in q);
  }

  /**
   * We need the agent's queues WITH their outbound addresses
   * (`outboundEmailAddress` / `outboundMessagingAddresses`), which is what the
   * share-link sms & email options filter on.
   *
   * `/routing/queues/me` says which queues the agent is a member of, but
   * returns a slim projection - id, name, division, auto-answer flags - with
   * no outbound addresses, so it cannot be used on its own. The full objects
   * only come from `/routing/queues`.
   *
   * Rather than pulling the org's entire queue list and intersecting it (orgs
   * with thousands of queues need 20+ paginated requests), we ask for the
   * member queues by id - `/routing/queues` accepts an `id` filter - so the
   * cost scales with the agent's membership, not the size of the org. The
   * full listing stays as a fallback in case the filtered call comes back
   * empty.
   */
  private async loadMyQueues(): Promise<IQueue[]> {
    try {
      const myQueues = await this.pureCloudService.getMyQueues();
      if (myQueues.length === 0) {
        this.queuesPromise = undefined;
        return [];
      }

      const ids = myQueues.map((q) => q.id);
      let queues: IQueue[] = await this.pureCloudService.getQueuesByIds(ids);

      if (queues.length === 0) {
        const allQueues = await this.pureCloudService.getQueues();
        queues = allQueues.filter((queue) => ids.includes(queue.id));
      }

      // an empty result usually means the api call failed (purecloud service
      // swallows errors and returns []). Don't cache it, or the sms/email
      // options stay hidden for the rest of the session.
      if (queues.length === 0) {
        this.queuesPromise = undefined;
      }

      return queues;
    } catch (err) {
      this.queuesPromise = undefined;
      throw err;
    }
  }
}
